/**
 * Temporary screen-scoped design tokens for the **Email configurations** step of
 * `/create/request?type=others`.
 *
 * Mirrors `Email Configurations.dc.html` 1:1 — every colour, size, radius,
 * spacing and type value the screen needs lives here so the components never
 * inline a literal. Once every screen is re-skinned these get promoted into
 * `src/theme` and this file is deleted (see UI_IMPLEMENTATION_RULES.md
 * § Temporary Screen Tokens).
 */

import { formField, inkfree, mobile, neutral, notification, signer, white } from "theme/colors";

/** The raw palette the design draws from. */
const palette = {
  ink: signer.ink,
  body: signer.text.secondary,
  muted: signer.text.faint,
  placeholder: formField.placeholderMuted,
  faint: mobile.base[100],
  borderLight: signer.border.light,
  borderMain: signer.disabled,
  surface: signer.surface.muted,
  surfaceFooter: signer.surface.subtle,
  white,
  /**
   * Fallback "brand colour" used by the email preview (CTA button, team
   * name, brand badge) when the account hasn't set custom branding — the
   * default for a REAL outgoing signature-request email, so it's the app's
   * actual brand red, not the internal design-mock orange every other
   * `accent` in this app's screen tokens uses for thin accents.
   */
  accent: inkfree.main,
  accentTint: signer.orange.tint,
  accentBorder: signer.orange.border,
  accentSoft: signer.orange.faintest,
  accentSoftBorder: signer.orange.progressTrack,
  rowDivider: signer.surface.muted,
  lock: neutral[400],
} as const;

export const emailConfigurationsTokens = {
  palette,

  /** Centred content column. */
  page: {
    maxWidth: 1220,
    paddingTop: "36px",
    paddingX: "40px",
    paddingBottom: "80px",
  },

  /** "Email configurations" title block. */
  header: {
    titleFontSize: "28px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    titleColor: palette.ink,
    titleMarginBottom: "6px",
    subtitleFontSize: "13px",
    subtitleColor: palette.muted,
    subtitleMarginBottom: "26px",
  },

  /** Accordion column beside the sticky preview. */
  layout: {
    previewWidth: 420,
    columnGap: "28px",
    sectionGap: "14px",
  },

  /** One collapsible settings card. */
  section: {
    radius: "12px",
    bg: palette.white,
    shadow: "0 1px 3px rgba(33,21,71,.05)",
    borderOpen: palette.accentBorder,
    borderClosed: palette.borderLight,
    headPadding: "16px 18px",
    headGap: "12px",
    iconTileSize: 34,
    iconTileRadius: "9px",
    iconTileBgOpen: palette.accentTint,
    iconTileBgClosed: palette.surface,
    iconColorOpen: palette.accent,
    iconColorClosed: palette.body,
    iconSize: 17,
    titleFontSize: "14.5px",
    titleFontWeight: 800,
    titleColor: palette.ink,
    subFontSize: "12px",
    subColor: palette.muted,
    subMarginTop: "2px",
    chevronSize: 13,
    chevronColor: palette.body,
    /** The design indents every body under the icon tile. */
    bodyPadding: "4px 18px 20px 64px",
    /** Toggle lists sit tighter — their last row supplies the gap. */
    bodyPaddingTight: "4px 18px 12px 64px",
    bodyGap: "16px",
  },

  /** Collapsed-state summary pill on the right of a section head. */
  badge: {
    fontSize: "11px",
    fontWeight: 700,
    color: palette.accent,
    bg: palette.accentTint,
    border: palette.accentBorder,
    radius: "999px",
    padding: "4px 10px",
  },

  /** Subject / message fields. */
  field: {
    gap: "6px",
    labelFontSize: "12.5px",
    labelFontWeight: 700,
    labelColor: palette.ink,
    requiredColor: palette.accent,
    counterFontSize: "11px",
    counterColor: palette.muted,
    /** Subject — a boxed input that mirrors the message field. */
    inputHeight: 40,
    inputBorder: palette.borderMain,
    inputBorderWidth: "1.5px",
    inputFocusBorder: palette.accent,
    inputFontSize: "13.5px",
    inputFontWeight: 600,
    inputColor: palette.ink,
    inputPaddingX: "12px",
    inputRadius: "9px",
    placeholderColor: palette.placeholder,
    /** Message. */
    areaRadius: "9px",
    areaPadding: "10px 12px",
    areaFontSize: "13px",
    areaLineHeight: 1.6,
    areaRows: 4,
    errorColor: notification.error.icon,
  },

  /** Pill switch used by the reminder and permission rows. */
  toggle: {
    width: 40,
    height: 23,
    radius: "999px",
    onBg: palette.accent,
    offBg: palette.borderMain,
    knobSize: 19,
    knobOffset: 2,
    knobOnLeft: 19,
    knobBg: palette.white,
    knobShadow: "0 1px 3px rgba(33,21,71,.25)",
  },

  /** Label + description block beside a switch or a numeric input. */
  settingRow: {
    gap: "12px",
    labelFontSize: "13px",
    labelFontWeight: 700,
    labelColor: palette.ink,
    descFontSize: "12px",
    descColor: palette.muted,
    descMarginTop: "2px",
    dividerColor: palette.borderLight,
  },

  /** Tinted panel holding the two reminder intervals. */
  nestedPanel: {
    padding: "14px",
    radius: "10px",
    bg: palette.accentSoft,
    border: palette.accentSoftBorder,
    gap: "12px",
    rowGap: "10px",
    labelFontSize: "12.5px",
    labelColor: palette.body,
    unitFontSize: "12.5px",
    unitColor: palette.muted,
    unitWidth: 44,
  },

  /** Small centred number box ("5", "3", "180"). */
  numberInput: {
    width: 64,
    wideWidth: 74,
    height: 34,
    radius: "8px",
    border: palette.borderMain,
    borderWidth: "1.5px",
    focusBorder: palette.accent,
    fontSize: "13px",
    fontWeight: 700,
    color: palette.ink,
    disabledColor: palette.faint,
  },

  /** One row of the signer-permission list. */
  optionRow: {
    padding: "12px 0",
    gap: "12px",
    divider: palette.rowDivider,
  },

  /** "Send immediately" / "Schedule for later" picker. */
  schedule: {
    introFontSize: "12.5px",
    introColor: palette.body,
    introLineHeight: 1.6,
    gap: "14px",
    cardsGap: "10px",
    cardPadding: "12px 14px",
    cardRadius: "10px",
    cardBorderWidth: "1.5px",
    cardBorderOn: palette.accent,
    cardBorderOff: palette.borderLight,
    cardBgOn: palette.accentSoft,
    cardBgOff: palette.white,
    cardGap: "10px",
    radioSize: 16,
    radioBorderOn: palette.accent,
    radioBorderOff: palette.faint,
    radioDotSize: 8,
    radioDotOn: palette.accent,
    labelFontSize: "13px",
    labelFontWeight: 700,
    labelColor: palette.ink,
    subFontSize: "11.5px",
    subColor: palette.muted,
    /** Chosen-date strip below the two cards. */
    pickedPadding: "12px 14px",
    pickedRadius: "10px",
    pickedBg: palette.accentSoft,
    pickedBorder: palette.accentSoftBorder,
    pickedGap: "10px",
    pickedIconSize: 15,
    pickedIconColor: palette.accent,
    pickedFieldHeight: 34,
    pickedFieldRadius: "8px",
    pickedFieldFontSize: "12.5px",
    pickedFieldFontWeight: 600,
    pickedNoteFontSize: "11px",
    pickedNoteColor: palette.muted,
    pickedNote: "All dates are EST",
  },

  /** Sticky "Email preview" panel. */
  preview: {
    radius: "14px",
    border: palette.borderLight,
    shadow: "0 10px 30px rgba(33,21,71,.08)",
    bg: palette.white,
    stickyTop: 0,
    headPadding: "13px 18px",
    headGap: "9px",
    headBg: palette.accent,
    headColor: palette.white,
    headIconSize: 15,
    headFontSize: "13px",
    headFontWeight: 800,
    liveFontSize: "10.5px",
    liveFontWeight: 700,
    liveBg: "rgba(255,255,255,.18)",
    liveRadius: "999px",
    livePadding: "3px 10px",
    metaPadding: "16px 20px",
    metaGap: "5px",
    metaFontSize: "12px",
    metaColor: palette.body,
    metaLabelColor: palette.ink,
    bodyPadding: "20px",
    bodyGap: "14px",
    greetFontSize: "13px",
    greetFontWeight: 600,
    greetColor: palette.ink,
    paraFontSize: "12.5px",
    paraColor: palette.body,
    paraLineHeight: 1.7,
    ctaHeight: 40,
    ctaPaddingX: "22px",
    ctaRadius: "9px",
    ctaFontSize: "13px",
    ctaFontWeight: 700,
    noteFontSize: "11.5px",
    noteColor: palette.muted,
    noteLineHeight: 1.7,
    signOffFontSize: "12px",
    signOffLineHeight: 1.6,
    teamFontWeight: 700,
    footerPadding: "12px 20px",
    footerBg: palette.surfaceFooter,
    footerGap: "6px",
    brandFontSize: "9px",
    brandFontWeight: 800,
    brandRadius: "4px",
    brandPadding: "2px 6px",
    esignFontSize: "10px",
    esignFontWeight: 700,
    esignColor: palette.body,
    copyrightFontSize: "10px",
    copyrightColor: palette.muted,
    logoMaxHeight: 30,
    logoMaxWidth: 120,
  },

  /** Feature-gate padlock beside a disabled control. */
  lock: {
    size: 16,
    color: palette.lock,
  },

  /**
   * eSign-mode section. Not part of `Email Configurations.dc.html` — it is kept
   * so the requester can still pick a signing mode, drawn with the design's
   * section chrome. The two security chips keep the colours the step used
   * before the re-skin.
   */
  esign: {
    radioPadding: "0 8px 0 0",
    chipHeight: 22,
    chipFontSize: "11px",
    chipFontWeight: 600,
    highChipBg: "#E8F5D2",
    highChipColor: "#85C935",
    basicChipBg: "#DEF2FF",
    basicChipColor: "#00B2FF",
    remainingWidth: 90,
  },
} as const;

export default emailConfigurationsTokens;
